import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { ramsService } from '../../../services/ramsService';

const RamsCard = ({ assignment, onAcknowledged }) => {
  const doc = assignment?.rams_document;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acknowledged, setAcknowledged] = useState(assignment?.acknowledged || false);
  const [acknowledgedAt, setAcknowledgedAt] = useState(assignment?.acknowledged_at || null);
  const [viewLoading, setViewLoading] = useState(false);

  const formatDate = (ts) => {
    if (!ts) return '';
    return new Date(ts)?.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleViewDocument = async () => {
    setError('');
    // Defense in depth — if the rams_documents embed returned null (e.g. RLS
    // denied, or the doc was deleted), fail loudly with an actionable message
    // rather than calling getSignedUrl(undefined) and surfacing a vague
    // storage error. This guards against future RLS misconfigurations.
    if (!doc?.file_path) {
      console.error('[RamsCard] view document: doc embed missing', { assignmentId: assignment?.id, ramsDocumentId: assignment?.rams_document_id });
      setError("This document's details are unavailable. Please contact your site admin — the assignment may reference a deleted document.");
      return;
    }

    // Open the destination tab synchronously inside the click handler so the
    // user-gesture chain is preserved across the upcoming `await`. Modern
    // browsers (Safari especially) silently block `window.open` if it runs
    // after an async hop — that's the primary reason the previous implementation
    // looked like "nothing happened" for workers. The empty tab will be
    // redirected once the signed URL resolves (~100–300ms).
    const newWin = window?.open('', '_blank', 'noopener,noreferrer');

    setViewLoading(true);
    const result = await ramsService?.getSignedUrl(doc?.file_path, doc?.title);
    setViewLoading(false);

    if (result?.url) {
      if (newWin) {
        newWin.location.href = result.url;
      } else {
        // Even the synchronous open was blocked (extreme browser settings).
        // Fall back to in-place navigation so the user still gets the file.
        // The signed URL is download-attachment so this won't actually leave
        // the SPA — the browser handles it as a download.
        window.location.href = result.url;
      }
      return;
    }

    // Signed URL failed — close the placeholder tab so the user isn't left
    // with a stranded about:blank, then surface the real error inline.
    if (newWin) newWin.close();

    const err = result?.error;
    console.error('[RamsCard] view document failed:', { filePath: doc?.file_path, code: err?.code, message: err?.message });
    if (err?.code === 'PGRST301') {
      setError(`Could not open document — permission denied: ${err?.message}`);
    } else {
      setError(`Could not open document: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleAcknowledge = async () => {
    setError('');
    // Same defensive guard as handleViewDocument. Without doc.id, the
    // INSERT into rams_acknowledgements would fail with a 23502 NOT NULL
    // violation on rams_document_id — the exact bug class fixed by
    // 20260428110000. Refuse the action with a clear message.
    if (!doc?.id) {
      console.error('[RamsCard] acknowledge: doc embed missing', { assignmentId: assignment?.id, ramsDocumentId: assignment?.rams_document_id });
      setError("Cannot acknowledge — this document's details are unavailable. Please contact your site admin.");
      return;
    }
    setLoading(true);
    const result = await ramsService?.acknowledgeRams(doc?.id, assignment?.worker_user_id);
    setLoading(false);
    if (result?.error) {
      const err = result?.error;
      console.error('[RamsCard] acknowledge failed:', { ramsId: doc?.id, code: err?.code, message: err?.message });
      if (err?.code === '23502') {
        // not-null violation — surface so the underlying FK bug
        // (rams_document_id null) is visible instead of generic copy.
        setError(`Failed to acknowledge — required field missing: ${err?.message}`);
      } else if (err?.code === 'PGRST301') {
        setError(`Failed to acknowledge — permission denied: ${err?.message}`);
      } else {
        setError(`Failed to acknowledge: ${err?.message || 'Unknown error'}`);
      }
      return;
    }
    const ts = result?.data?.acknowledged_at || new Date()?.toISOString();
    setAcknowledged(true);
    setAcknowledgedAt(ts);
    onAcknowledged?.(doc?.id);
  };

  return (
    <div className={`bg-card border rounded-xl p-5 transition-all ${
      acknowledged ? 'border-success/30' : 'border-amber-300'
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            acknowledged ? 'bg-success/10' : 'bg-amber-50'
          }`}>
            <Icon
              name={acknowledged ? 'FileCheck' : 'FileText'}
              size={20}
              className={acknowledged ? 'text-success' : 'text-amber-600'}
            />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base leading-tight">{doc?.title}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {doc?.site?.site_name && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="MapPin" size={11} />
                  {doc?.site?.site_name}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Icon name="Calendar" size={11} />
                Uploaded {formatDate(doc?.created_at)?.split(',')?.[0]}
              </span>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        {acknowledged ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-success/10 text-success shrink-0">
            <Icon name="CheckCircle" size={12} />
            Confirmed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 shrink-0">
            <Icon name="Clock" size={12} />
            Pending
          </span>
        )}
      </div>
      {/* Confirmed timestamp */}
      {acknowledged && acknowledgedAt && (
        <div className="flex items-center gap-2 bg-success/5 border border-success/20 rounded-lg px-3 py-2 mb-4">
          <Icon name="CheckCircle" size={14} className="text-success shrink-0" />
          <p className="text-xs text-success font-medium">
            You confirmed reading this document on {formatDate(acknowledgedAt)}
          </p>
        </div>
      )}
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 mb-4">
          <Icon name="AlertCircle" size={14} className="text-destructive shrink-0" />
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          onClick={handleViewDocument}
          disabled={viewLoading}
          className="flex-1 h-10"
        >
          {viewLoading ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-2" />Opening...</>
          ) : (
            <><Icon name="ExternalLink" size={15} className="mr-2" />View Document</>
          )}
        </Button>

        {!acknowledged && (
          <Button
            onClick={handleAcknowledge}
            disabled={loading}
            className="flex-1 h-10 bg-success hover:bg-success/90 text-white"
          >
            {loading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Confirming...</>
            ) : (
              <><Icon name="CheckCircle" size={15} className="mr-2" />I confirm I have read and understood this H&S document</>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default RamsCard;
